import { CurrencyCode, SubscriptionType } from '../../gen/types'
import { isTeamSubscriptionType, TEAM_MONTHLY_SUBSCRIPTION_TYPE } from '../../subscription-types'
import type { PaymentSubscriptionType } from '../../subscription-types'
import type { Cost } from './pricing'
import { cfCosts, getCostForCurrency } from './pricing'

const COIN_SUBSCRIPTION_PLANS = [
  'coinPack25k',
  'coinPack100k',
  'coinPack250k',
  'coinPack400k',
  'coinPack700k',
  'coinPack1M',
] as const

export type CoinSubscriptionPlan = (typeof COIN_SUBSCRIPTION_PLANS)[number]
export type TeamSubscriptionPlan = 'teamMonthly'

export type DefaultPlan =
  | 'freeTrial'
  | 'yearly'
  | 'monthly'
  | 'freeTrialYearly'
  | 'aiStudio'
  | TeamSubscriptionPlan
  | CoinSubscriptionPlan

export interface Plan {
  id: PaymentSubscriptionType
  name: string
  cost: Cost
}

export type SelectionPlanLabel =
  | { kind: 'coin_pack'; amount: number }
  | { kind: 'team_subscription' }
  | { kind: 'standard'; cadence: 'monthly' | 'annual'; freeTrial: boolean }

export function isCoinSubscriptionPlan(plan: string): plan is CoinSubscriptionPlan {
  return (COIN_SUBSCRIPTION_PLANS as readonly string[]).includes(plan)
}

export function isTeamSubscriptionPlan(plan: string): plan is TeamSubscriptionPlan {
  return plan === TEAM_MONTHLY_SUBSCRIPTION_TYPE
}

export const coinPlans = {
  coinPack25k: {
    id: SubscriptionType.CoinPackMonthly25k,
    name: '25,000 coins/month' as const,
    cost: { [CurrencyCode.Usd]: 3.99, [CurrencyCode.Eur]: 3.99, [CurrencyCode.Gbp]: 3.99 },
  },
  coinPack100k: {
    id: SubscriptionType.CoinPackMonthly100k,
    name: '100,000 coins/month' as const,
    cost: { [CurrencyCode.Usd]: 9.99, [CurrencyCode.Eur]: 9.99, [CurrencyCode.Gbp]: 9.99 },
  },
  coinPack250k: {
    id: SubscriptionType.CoinPackMonthly250k,
    name: '250,000 coins/month' as const,
    cost: { [CurrencyCode.Usd]: 19.99, [CurrencyCode.Eur]: 19.99, [CurrencyCode.Gbp]: 19.99 },
  },
  coinPack400k: {
    id: SubscriptionType.CoinPackMonthly400k,
    name: '400,000 coins/month' as const,
    cost: { [CurrencyCode.Usd]: 29.99, [CurrencyCode.Eur]: 29.99, [CurrencyCode.Gbp]: 29.99 },
  },
  coinPack700k: {
    id: SubscriptionType.CoinPackMonthly700k,
    name: '700,000 coins/month' as const,
    cost: { [CurrencyCode.Usd]: 49.99, [CurrencyCode.Eur]: 49.99, [CurrencyCode.Gbp]: 49.99 },
  },
  coinPack1M: {
    id: SubscriptionType.CoinPackMonthly1M,
    name: '1,000,000 coins/month' as const,
    cost: { [CurrencyCode.Usd]: 69.99, [CurrencyCode.Eur]: 69.99, [CurrencyCode.Gbp]: 69.99 },
  },
} as const satisfies Record<CoinSubscriptionPlan, Plan>

export function getPlans() {
  const monthlyFreeTrialPlan = {
    id: SubscriptionType.AllAccessMonthlyFreeTrial,
    name: '{monthly} {free_trial}' as const,
    cost: cfCosts.monthly,
  }

  const monthlyPlan = {
    id: SubscriptionType.AllAccessMonthly,
    name: '{monthly}' as const,
    cost: cfCosts.monthly,
  }

  const yearlyFreeTrialPlan = {
    id: SubscriptionType.AllAccessYearlyFreeTrial,
    name: '{annual} {free_trial}' as const,
    cost: cfCosts.yearlyTrial,
  }

  const yearlyPlan = {
    id: SubscriptionType.AllAccessYearlyPromo,
    name: '{annual} {subscription}' as const,
    cost: cfCosts.promotion,
  }

  const aiStudioTrial = {
    id: SubscriptionType.StudioAiYearlyFreeTrial,
    name: '{annual} {free_trial}' as const,
    cost: cfCosts.aiStudioTrial,
  }

  const aiStudio = {
    id: SubscriptionType.StudioAiYearly,
    name: '{annual} {subscription}' as const,
    cost: cfCosts.aiStudio,
  }

  const teamMonthly = {
    id: TEAM_MONTHLY_SUBSCRIPTION_TYPE,
    name: '{team_subscription}' as const,
    cost: cfCosts.teamMonthly,
  }

  return {
    monthlyFreeTrialPlan,
    monthlyPlan,
    yearlyFreeTrialPlan,
    yearlyPlan,
    aiStudioTrial,
    aiStudio,
    teamMonthly,
    coinPlans,
  }
}

export function decidePlans({
  isEligibleForAllAccessFreeTrial,
  isEligibleForStudioAiFreeTrial,
  defaultPlan,
}: {
  isEligibleForAllAccessFreeTrial: boolean | undefined
  isEligibleForStudioAiFreeTrial: boolean | undefined
  defaultPlan?: DefaultPlan
}) {
  const {
    monthlyFreeTrialPlan,
    monthlyPlan,
    yearlyFreeTrialPlan,
    yearlyPlan,
    aiStudioTrial,
    aiStudio,
    teamMonthly,
    coinPlans: configuredCoinPlans,
  } = getPlans()

  if (defaultPlan && isCoinSubscriptionPlan(defaultPlan)) {
    return [configuredCoinPlans[defaultPlan]]
  }

  if (defaultPlan && isTeamSubscriptionPlan(defaultPlan)) {
    return [teamMonthly]
  }

  if (defaultPlan === 'monthly') {
    return isEligibleForAllAccessFreeTrial ? [yearlyPlan, monthlyFreeTrialPlan] : [monthlyPlan]
  }

  if (defaultPlan === 'yearly') {
    return [yearlyPlan]
  }

  if (defaultPlan === 'aiStudio') {
    return isEligibleForStudioAiFreeTrial ? [aiStudioTrial] : [aiStudio]
  }

  return isEligibleForAllAccessFreeTrial ? [yearlyFreeTrialPlan] : [yearlyPlan]
}

export function getDefaultSubscriptionType(plans: Plan[]) {
  return plans.length === 1 ? plans[0].id : plans[1].id
}

export function getPlanForSubscriptionType(
  plans: Plan[],
  subscriptionType: PaymentSubscriptionType,
) {
  return plans.find(plan => plan.id === subscriptionType)
}

export function getPlanCost(
  plans: Plan[],
  subscriptionType: PaymentSubscriptionType,
  currencyCode: Parameters<typeof getCostForCurrency>[1],
  fallback = 0,
) {
  return getCostForCurrency(
    getPlanForSubscriptionType(plans, subscriptionType)?.cost,
    currencyCode,
    fallback,
  )
}

export function getSelectionPlanLabel(
  subscriptionType: PaymentSubscriptionType,
): SelectionPlanLabel {
  if (isTeamSubscriptionType(subscriptionType)) {
    return { kind: 'team_subscription' }
  }

  switch (subscriptionType) {
    case SubscriptionType.CoinPackMonthly25k: {
      return { kind: 'coin_pack', amount: 25_000 }
    }
    case SubscriptionType.CoinPackMonthly100k: {
      return { kind: 'coin_pack', amount: 100_000 }
    }
    case SubscriptionType.CoinPackMonthly250k: {
      return { kind: 'coin_pack', amount: 250_000 }
    }
    case SubscriptionType.CoinPackMonthly400k: {
      return { kind: 'coin_pack', amount: 400_000 }
    }
    case SubscriptionType.CoinPackMonthly700k: {
      return { kind: 'coin_pack', amount: 700_000 }
    }
    case SubscriptionType.CoinPackMonthly1M: {
      return { kind: 'coin_pack', amount: 1_000_000 }
    }
    case SubscriptionType.AllAccessMonthlyFreeTrial: {
      return { kind: 'standard', cadence: 'monthly', freeTrial: true }
    }
    case SubscriptionType.AllAccessYearlyFreeTrial:
    case SubscriptionType.StudioAiYearlyFreeTrial: {
      return { kind: 'standard', cadence: 'annual', freeTrial: true }
    }
    case SubscriptionType.AllAccessYearly:
    case SubscriptionType.AllAccessYearlyPromo:
    case SubscriptionType.StudioAiYearly: {
      return { kind: 'standard', cadence: 'annual', freeTrial: false }
    }
    default: {
      return { kind: 'standard', cadence: 'monthly', freeTrial: false }
    }
  }
}
